<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  CheckCircle2,
  Eye,
  FilePlus2,
  Plus,
  Save,
  Send,
  Trash2,
} from "lucide-vue-next";
import type { Project, ProjectRequirement, ProjectTask, V2Asset } from "@/types";
import { useProjectStore } from "@/stores/projects";
import { v2 } from "@/services/v2";
import AssetPreviewModal from "@/components/AssetPreviewModal.vue";

const props = defineProps<{ project: Project }>();
const emit = defineEmits<{ submitted: []; changed: [] }>();
const store = useProjectStore();
const title = ref("");
const plannedStartAt = ref("");
const plannedEndAt = ref("");
const newTaskTitle = ref("");
const newGroupTitles = ref<Record<number, string>>({});
const newChildTitles = ref<Record<number, string>>({});
const taskForms = ref<Record<number, { title: string; scope: string; acceptance: string; plannedStartAt: string; plannedEndAt: string }>>({});
const todoForms = ref<Record<number, { title: string; description: string; acceptance: string; plannedStartAt: string; plannedEndAt: string }>>({});
const requirements = ref<ProjectRequirement[]>([]);
const saving = ref(false);
const previewAsset = ref<V2Asset | null>(null);

const validation = computed(() => store.validation);
function lines(value: string) {
  return value.split("\n").map((item) => item.trim()).filter(Boolean);
}
function syncForms() {
  title.value = props.project.title;
  plannedStartAt.value = props.project.planned_start_at || "";
  plannedEndAt.value = props.project.planned_end_at || props.project.expected_at || "";
  taskForms.value = Object.fromEntries(
    props.project.tasks.map((task) => [
      task.id,
      {
        title: task.client_title || task.title,
        scope: task.client_scope || task.scope,
        acceptance: (task.client_acceptance_criteria?.length
          ? task.client_acceptance_criteria
          : task.acceptance_criteria || []
        ).join("\n"),
        plannedStartAt: task.planned_start_at || "",
        plannedEndAt: task.planned_end_at || task.expected_at || "",
      },
    ]),
  );
  todoForms.value = Object.fromEntries(
    props.project.tasks.flatMap((task) =>
      task.checklist.map((item) => [
        item.id,
        {
          title: item.title,
          description: item.description,
          acceptance: item.acceptance_criteria.join("\n"),
          plannedStartAt: item.planned_start_at || "",
          plannedEndAt: item.planned_end_at || "",
        },
      ]),
    ),
  );
  requirements.value = (props.project.reviewRequirements || []).map((item) => ({ ...item }));
}
watch(() => [props.project.id, props.project.updated_at, props.project.tasks.length], syncForms, { immediate: true });

async function run(action: () => Promise<unknown>) {
  saving.value = true;
  try {
    await action();
    emit("changed");
  } finally {
    saving.value = false;
  }
}
async function saveMeta() {
  await run(() =>
    store.updateDraft({
      title: title.value,
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
      rowVersion: props.project.row_version,
    }),
  );
}
async function saveRequirements() {
  await run(() =>
    store.updateDraft({
      title: title.value,
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
      reviewRequirements: requirements.value,
      rowVersion: props.project.row_version,
    }),
  );
}
function addRequirement() {
  requirements.value.push({
    id: `manual-${Date.now()}`,
    category: "attention",
    title: "",
    description: "",
    confirmed: false,
  });
}
function removeRequirement(id: string) {
  requirements.value = requirements.value.filter((item) => item.id !== id);
}
async function saveTask(taskId: number, rowVersion: number) {
  const form = taskForms.value[taskId];
  if (!form) return;
  await run(() =>
    store.updateTask(taskId, {
      title: form.title,
      scope: form.scope,
      acceptanceCriteria: lines(form.acceptance),
      clientAcceptanceCriteria: lines(form.acceptance),
      plannedStartAt: form.plannedStartAt || null,
      plannedEndAt: form.plannedEndAt || null,
      rowVersion,
    }),
  );
}
async function addTask() {
  if (!newTaskTitle.value.trim()) return;
  const value = newTaskTitle.value;
  newTaskTitle.value = "";
  await run(() =>
    store.addTask({
      title: value,
      scope: "",
      deliverables: [],
      acceptanceCriteria: ["交付内容与确认方案一致"],
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
    }),
  );
}
async function saveTodo(itemId: number, rowVersion: number) {
  const form = todoForms.value[itemId];
  if (!form) return;
  await run(() =>
    store.updateChecklist(itemId, {
      title: form.title,
      description: form.description,
      acceptanceCriteria: lines(form.acceptance),
      plannedStartAt: form.plannedStartAt || null,
      plannedEndAt: form.plannedEndAt || null,
      rowVersion,
    }),
  );
}
async function addTodo(taskId: number, parentItemId?: number) {
  const source = parentItemId ? newChildTitles : newGroupTitles;
  const key = parentItemId || taskId;
  const value = source.value[key]?.trim();
  if (!value) return;
  source.value[key] = "";
  await run(() =>
    store.addChecklist(taskId, {
      title: value,
      parentItemId: parentItemId || null,
      description: parentItemId
        ? `完成“${value}”并提交可核查结果。`
        : `完成“${value}”阶段，并确保所有子步骤可核查。`,
      acceptanceCriteria: ["按客户确认要求完成并通过核查"],
      plannedStartAt: plannedStartAt.value || null,
      plannedEndAt: plannedEndAt.value || null,
    }),
  );
}
function checklistGroups(task: ProjectTask) {
  return task.checklist.filter((item) => !item.parent_item_id);
}
function checklistChildren(task: ProjectTask, parentId: number) {
  return task.checklist.filter((item) => Number(item.parent_item_id) === parentId);
}
async function uploadTaskAsset(taskId: number, event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  await run(async () => {
    await v2.uploadTaskAsset(taskId, file);
    await store.loadDraft(props.project.id);
  });
  input.value = "";
}
async function archiveConversationAsset(assetId: number, taskId: number) {
  await run(async () => {
    await v2.linkAssetToTask(assetId, taskId);
    await store.loadDraft(props.project.id);
  });
}
async function submit() {
  await run(async () => {
    await store.submit();
    emit("submitted");
  });
}
</script>

<template>
  <div class="draft-page">
    <header class="draft-head">
      <div>
        <span>项目草稿</span>
        <h1>{{ project.title }}</h1>
        <p>补充正式素材、日期和执行细节，检查无误后再提交给团队。</p>
      </div>
      <button class="btn btn--primary submit" :disabled="saving || !validation.valid" @click="submit">
        <Send :size="17" />正式提交项目
      </button>
    </header>

    <section class="meta-band">
      <label>项目名称<input v-model="title" class="input" /></label>
      <label>计划开始<input v-model="plannedStartAt" class="input" type="date" /></label>
      <label>期望完成<input v-model="plannedEndAt" class="input" type="date" /></label>
      <button class="btn" :disabled="saving" @click="saveMeta"><Save :size="16" />保存项目信息</button>
    </section>

    <section v-if="!validation.valid" class="validation">
      <b>提交前还需处理 {{ validation.errors.length }} 项</b>
      <ul><li v-for="error in validation.errors" :key="`${error.code}-${error.taskId || 0}`">{{ error.message }}</li></ul>
    </section>
    <section v-else class="validation valid"><CheckCircle2 :size="18" /><b>提交检查已通过</b></section>

    <section class="solution-band">
      <h2>已确认完整方案</h2>
      <p>{{ project.solution?.clientContent.fullPlan || project.solution?.clientContent.summary }}</p>
    </section>

    <section class="requirements-review">
      <header>
        <div><h2>客户明确要求核查</h2><p>内容、台词、主题、限制和注意事项可修改；逐项确认后才能正式提交。</p></div>
        <button class="btn" type="button" @click="addRequirement"><Plus :size="16" />新增核查项</button>
      </header>
      <div v-for="requirement in requirements" :key="requirement.id" class="requirement-row">
        <select v-model="requirement.category" class="select" aria-label="要求类别">
          <option value="attention">注意事项</option><option value="content">内容要求</option>
          <option value="dialogue">台词文案</option><option value="theme">主题方向</option>
          <option value="other">其他要求</option>
        </select>
        <input v-model="requirement.title" class="input" placeholder="核查事项" />
        <textarea v-model="requirement.description" class="textarea" placeholder="客户明确要求和具体限制"></textarea>
        <label class="confirm-check"><input v-model="requirement.confirmed" type="checkbox" />已核实无误</label>
        <button class="icon-btn danger" title="删除核查项" @click="removeRequirement(requirement.id)"><Trash2 :size="16" /></button>
      </div>
      <button class="btn btn--primary save-requirements" :disabled="saving" @click="saveRequirements"><Save :size="16" />保存核查清单</button>
    </section>

    <section class="tasks">
      <header><div><h2>执行任务与两级 To-do</h2><p>{{ project.tasks.length }} 个任务，一级为执行阶段，二级为具体步骤</p></div></header>
      <article v-for="(task, index) in project.tasks" :key="task.id" class="task-block">
        <header><span>{{ index + 1 }}</span><b>{{ taskForms[task.id]?.title }}</b><button class="icon-btn danger" title="删除任务" @click="run(() => store.deleteTask(task.id))"><Trash2 :size="16" /></button></header>
        <div class="task-fields">
          <label>任务名称<input v-model="taskForms[task.id].title" class="input" /></label>
          <label class="wide">执行说明<textarea v-model="taskForms[task.id].scope" class="textarea"></textarea></label>
          <label class="wide">验收标准（每行一项）<textarea v-model="taskForms[task.id].acceptance" class="textarea small"></textarea></label>
          <label>计划开始<input v-model="taskForms[task.id].plannedStartAt" class="input" type="date" /></label>
          <label>计划完成<input v-model="taskForms[task.id].plannedEndAt" class="input" type="date" /></label>
          <button class="btn save-task" :disabled="saving" @click="saveTask(task.id, task.row_version)"><Save :size="16" />保存任务</button>
        </div>
        <section class="todo-list">
          <h3>两级执行清单</h3>
          <article v-for="group in checklistGroups(task)" :key="group.id" class="todo-group">
            <div class="todo-fields todo-fields--group">
              <span class="level-tag">一级</span>
              <input v-model="todoForms[group.id].title" class="input" aria-label="执行阶段标题" />
              <textarea v-model="todoForms[group.id].description" class="textarea" placeholder="阶段目标和交付结果"></textarea>
              <textarea v-model="todoForms[group.id].acceptance" class="textarea" placeholder="阶段核查标准，每行一项"></textarea>
              <label>开始<input v-model="todoForms[group.id].plannedStartAt" class="input" type="date" /></label>
              <label>完成<input v-model="todoForms[group.id].plannedEndAt" class="input" type="date" /></label>
              <button class="icon-btn" title="保存执行阶段" @click="saveTodo(group.id, group.row_version)"><Save :size="16" /></button>
              <button class="icon-btn danger" title="删除执行阶段及子步骤" @click="run(() => store.deleteChecklist(group.id))"><Trash2 :size="16" /></button>
            </div>
            <div v-for="item in checklistChildren(task, group.id)" :key="item.id" class="todo-fields todo-fields--child">
              <span class="level-tag">二级</span>
              <input v-model="todoForms[item.id].title" class="input" aria-label="具体执行步骤" />
              <textarea v-model="todoForms[item.id].description" class="textarea" placeholder="具体怎么做、输入和输出是什么"></textarea>
              <textarea v-model="todoForms[item.id].acceptance" class="textarea" placeholder="核查标准，每行一项"></textarea>
              <label>开始<input v-model="todoForms[item.id].plannedStartAt" class="input" type="date" /></label>
              <label>完成<input v-model="todoForms[item.id].plannedEndAt" class="input" type="date" /></label>
              <button class="icon-btn" title="保存具体步骤" @click="saveTodo(item.id, item.row_version)"><Save :size="16" /></button>
              <button class="icon-btn danger" title="删除具体步骤" @click="run(() => store.deleteChecklist(item.id))"><Trash2 :size="16" /></button>
            </div>
            <div class="add-row add-row--child"><input v-model="newChildTitles[group.id]" class="input" placeholder="新增二级执行步骤" @keyup.enter="addTodo(task.id, group.id)" /><button class="btn" @click="addTodo(task.id, group.id)"><Plus :size="16" />添加步骤</button></div>
          </article>
          <div class="add-row"><input v-model="newGroupTitles[task.id]" class="input" placeholder="新增一级执行阶段" @keyup.enter="addTodo(task.id)" /><button class="btn" @click="addTodo(task.id)"><Plus :size="16" />添加阶段</button></div>
        </section>
        <section class="assets">
          <header><h3>正式执行素材</h3><label class="btn upload"><FilePlus2 :size="16" />上传素材<input type="file" @change="uploadTaskAsset(task.id, $event)" /></label></header>
          <div class="asset-list">
            <button v-for="asset in task.assets" :key="asset.id" class="asset-row" @click="previewAsset = asset"><span>{{ asset.originalName }}</span><Eye :size="16" /></button>
            <p v-if="!task.assets.length" class="muted">尚未归档正式素材。</p>
          </div>
          <div v-if="project.conversationAssets.length" class="conversation-assets">
            <small>对话参考可归档到此任务</small>
            <button v-for="asset in project.conversationAssets.filter(asset => !task.assets.some(item => item.id === asset.id))" :key="asset.id" class="btn" @click="archiveConversationAsset(asset.id, task.id)">归档 {{ asset.originalName }}</button>
          </div>
        </section>
      </article>
      <div class="add-task"><input v-model="newTaskTitle" class="input" placeholder="新增任务名称" @keyup.enter="addTask" /><button class="btn" @click="addTask"><Plus :size="16" />添加任务</button></div>
    </section>
    <AssetPreviewModal :asset="previewAsset" @close="previewAsset = null" />
  </div>
</template>

<style scoped>
.draft-page{min-height:calc(100vh - 64px);background:#f5f7f7}.draft-head{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;padding:26px 30px;background:#fff;border-bottom:1px solid var(--line)}.draft-head span{color:var(--brand-dark);font-size:12px;font-weight:700}.draft-head h1{margin:3px 0;font-size:24px}.draft-head p,.tasks header p,.requirements-review header p{margin:0;color:var(--muted)}.submit,.meta-band .btn,.save-task,.add-row .btn,.add-task .btn,.upload,.requirements-review .btn{display:inline-flex;align-items:center;justify-content:center;gap:7px}.meta-band{display:grid;grid-template-columns:minmax(220px,1fr) 180px 180px auto;align-items:end;gap:12px;padding:18px 30px;background:#fff;border-bottom:1px solid var(--line)}label{display:grid;gap:5px;color:var(--muted);font-size:12px}.validation,.solution-band,.requirements-review,.tasks{margin:18px 30px;padding:17px 20px;background:#fff;border:1px solid var(--line)}.validation{border-left:4px solid #d98b21}.validation.valid{display:flex;align-items:center;gap:8px;border-left-color:var(--success);color:var(--success)}.validation ul{margin:8px 0 0;padding-left:18px}.solution-band h2,.requirements-review h2,.tasks h2,.task-block h3{margin:0}.solution-band p{white-space:pre-wrap;line-height:1.7}.requirements-review{display:grid;gap:10px}.requirements-review>header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.requirement-row{display:grid;grid-template-columns:130px minmax(150px,.7fr) minmax(240px,1.3fr) 120px 36px;align-items:center;gap:7px;padding:9px 0;border-top:1px solid var(--line)}.requirement-row .textarea{min-height:58px}.confirm-check{display:flex;align-items:center;gap:6px;color:var(--ink)}.save-requirements{justify-self:start}.tasks{display:grid;gap:14px}.tasks>header{display:flex;justify-content:space-between}.task-block{border-top:2px solid var(--ink);padding:0 0 18px;background:#fff}.task-block>header{display:grid;grid-template-columns:30px minmax(0,1fr) auto;align-items:center;gap:10px;min-height:48px;border-bottom:1px solid var(--line)}.task-block>header>span{display:grid;width:26px;height:26px;place-items:center;background:var(--ink);color:#fff}.icon-btn{display:grid;width:36px;height:36px;place-items:center;border:1px solid var(--line);background:#fff}.icon-btn.danger{color:var(--danger)}.task-fields{display:grid;grid-template-columns:1fr 1fr auto;align-items:end;gap:10px;padding:14px 0}.task-fields .wide{grid-column:span 2}.textarea.small{min-height:64px}.todo-list,.assets{padding:14px 0;border-top:1px solid var(--line)}.todo-list h3,.assets h3{font-size:15px}.todo-group{margin-top:10px;border-left:3px solid #87979d;padding-left:10px}.todo-fields{display:grid;grid-template-columns:44px minmax(150px,.7fr) minmax(220px,1.2fr) minmax(180px,1fr) 135px 135px 36px 36px;align-items:end;gap:7px;padding:9px 0;border-bottom:1px solid var(--line)}.todo-fields--child{margin-left:22px;border-left:1px solid var(--line);padding-left:10px}.todo-fields .textarea{min-height:62px}.level-tag{align-self:center;padding:4px 5px;background:#eef2f2;color:var(--muted);font-size:11px;text-align:center}.todo-fields--child .level-tag{background:#eaf3f8;color:#2f647f}.add-row,.add-task{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;margin-top:10px}.add-row--child{margin-left:32px}.assets>header{display:flex;align-items:center;justify-content:space-between}.upload input{display:none}.asset-list{display:flex;flex-wrap:wrap;gap:7px;margin-top:9px}.asset-row{display:flex;align-items:center;gap:8px;border:1px solid var(--line);padding:8px 10px;background:#fff}.conversation-assets{display:flex;align-items:center;flex-wrap:wrap;gap:7px;margin-top:10px;padding-top:10px;border-top:1px dashed var(--line)}.conversation-assets small{width:100%;color:var(--muted)}@media(max-width:1150px){.requirement-row{grid-template-columns:130px 1fr 1fr 36px}.requirement-row .textarea{grid-column:2/4}.confirm-check{grid-column:2}.todo-fields{grid-template-columns:44px minmax(180px,1fr) 1fr 1fr 36px 36px}.todo-fields label{grid-row:2}.todo-fields .textarea{grid-row:2}.todo-fields .icon-btn{grid-row:1}.todo-fields .icon-btn:last-child{grid-column:6}}@media(max-width:850px){.meta-band{grid-template-columns:1fr 1fr}.task-fields{grid-template-columns:1fr}.task-fields .wide{grid-column:auto}.draft-head{padding:20px 16px}.meta-band,.validation,.solution-band,.requirements-review,.tasks{margin-left:14px;margin-right:14px}.meta-band{padding:16px}.requirement-row{grid-template-columns:1fr 36px}.requirement-row .select,.requirement-row .input,.requirement-row .textarea,.confirm-check{grid-column:1}.requirement-row .icon-btn{grid-column:2;grid-row:1}.todo-fields{grid-template-columns:42px minmax(0,1fr) 36px 36px}.todo-fields .input{grid-column:2}.todo-fields .textarea,.todo-fields label{grid-column:2/-1;grid-row:auto}.todo-fields .icon-btn{grid-row:1}.todo-fields .icon-btn:last-child{grid-column:4}.todo-fields--child{margin-left:10px}.add-row--child{margin-left:10px}}@media(max-width:560px){.draft-head{display:grid}.submit{width:100%}.meta-band{grid-template-columns:1fr}.add-row,.add-task{grid-template-columns:1fr}.requirements-review>header{display:grid}.assets>header{align-items:flex-start}.conversation-assets .btn{max-width:100%;overflow:hidden;text-overflow:ellipsis}}
</style>
